import React from 'react';
import { InlineEditableCell } from './inline-editable-cell';
import { Badge } from '@/components/ui/badge';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface CFLineItem {
  id: string;
  financial_period_id: string;
  line_item: string;
  amount: number;
  is_manual_override: boolean;
  notes?: string | null;
  is_calculated: boolean;
}

export interface EditableCFRowProps {
  lineItem: CFLineItem;
  lineItemLabel: string;
  onUpdate?: (id: string, data: Partial<CFLineItem>) => Promise<void>;
  onClearOverride?: (id: string) => Promise<void>;
  disabled?: boolean;
}

/**
 * Editable Cash Flow row component with override capability
 *
 * Features:
 * - Auto-calculated by default (grey "Auto" badge)
 * - Click-to-edit for manual override (blue "Manual" badge)
 * - Clear button to revert to auto-calculated
 * - Auto-save on blur/Enter
 * - Optimistic updates
 *
 * Layout: | Line Item Label | Amount (with badge) | Actions |
 */
export const EditableCFRow: React.FC<EditableCFRowProps> = ({
  lineItem,
  lineItemLabel,
  onUpdate,
  onClearOverride,
  disabled = false,
}) => {
  const queryClient = useQueryClient();

  // Update amount mutation (creates manual override)
  const updateAmount = useMutation({
    mutationFn: async (newAmount: number) => {
      if (!onUpdate) throw new Error('Update function not provided');
      await onUpdate(lineItem.id, {
        amount: newAmount,
        is_manual_override: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashflow', lineItem.financial_period_id] });
    },
  });

  // Clear override mutation (reverts to auto-calculated)
  const clearOverride = useMutation({
    mutationFn: async () => {
      if (!onClearOverride) throw new Error('Clear override function not provided');
      await onClearOverride(lineItem.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashflow', lineItem.financial_period_id] });
    },
  });

  const handleAmountSave = async (newValue: string | number) => {
    const numValue = typeof newValue === 'string' ? parseFloat(newValue) : newValue;
    if (isNaN(numValue)) throw new Error('Invalid amount');
    await updateAmount.mutateAsync(numValue);
  };

  const handleClearOverride = async () => {
    await clearOverride.mutateAsync();
  };

  const isDisabled = disabled || lineItem.is_calculated;

  return (
    <tr className="editable-cf-row hover:bg-gray-50">
      <td className="py-1 px-2 border-b border-gray-200">
        <span className="text-gray-700">{lineItemLabel}</span>
        {lineItem.is_calculated && (
          <span className="ml-2 text-xs text-gray-400 italic">(calculated total)</span>
        )}
      </td>

      <td className="py-1 px-2 border-b border-gray-200 text-right">
        <div className="flex items-center justify-end gap-2">
          <InlineEditableCell
            value={lineItem.amount}
            onSave={handleAmountSave}
            cellType="currency"
            disabled={isDisabled}
            placeholder="$0.00"
          />

          {lineItem.is_manual_override ? (
            <Badge variant="default" className="bg-blue-500 text-white">Manual</Badge>
          ) : (
            <Badge variant="secondary" className="bg-gray-500 text-white">Auto</Badge>
          )}
        </div>
      </td>

      <td className="py-1 px-2 border-b border-gray-200 text-center">
        {lineItem.is_manual_override && !isDisabled && (
          <button
            onClick={handleClearOverride}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
            disabled={clearOverride.isPending}
          >
            {clearOverride.isPending ? 'Reverting...' : 'Revert to Auto'}
          </button>
        )}
      </td>
    </tr>
  );
};
